'use client';

// Shared map of which tickers have recent News Room (live Alpaca/Benzinga) news,
// so dashboard widgets (movers, rvol, etc.) can badge them. A single global SWR
// key means every consumer shares ONE deduped fetch; 60s refresh. Returns an
// empty map until loaded / when the live feed is empty (e.g. not configured) —
// callers then just render no badge.

import { useMemo } from 'react';
import useSWR from 'swr';
import { getLiveNews, type LiveNewsItem } from '@/lib/api-client';

/** symbol (UPPERCASE) → its recent live-news items (feed order, newest first). */
export function buildNewsSymbolMap(items: LiveNewsItem[]): Map<string, LiveNewsItem[]> {
  const map = new Map<string, LiveNewsItem[]>();
  for (const item of items) {
    for (const sym of item.symbols ?? []) {
      const key = sym.toUpperCase();
      const arr = map.get(key);
      if (arr) arr.push(item);
      else map.set(key, [item]);
    }
  }
  return map;
}

const EMPTY_MAP: Map<string, LiveNewsItem[]> = new Map();

export function useLiveNewsSymbols(): Map<string, LiveNewsItem[]> {
  const { data } = useSWR(
    'live-news-symbols',
    () => getLiveNews({ limit: 100 }),
    { refreshInterval: 60_000, revalidateOnFocus: false, dedupingInterval: 30_000 },
  );
  return useMemo(() => (data ? buildNewsSymbolMap(data.items) : EMPTY_MAP), [data]);
}
